import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/TextField';
import { Textarea } from '@/components/ui/Textarea';
import { Checkbox } from '@/components/ui/Checkbox';
import { Select } from '@/components/ui/Select';
import { UsersIcon } from '@/components/icons';
import { httpClient as http } from '@/lib/api';
import {
  useAccess,
  canManageAccess,
  canDelegateRole,
  type Overview,
  type AccessRole,
  type Team,
  type Grant,
  type Staff,
} from './access';
import { RolePermissionsEditor } from './RolePermissionsEditor';
import { StaffCreateForm } from './StaffCreateForm';
import './AccessPage.css';

type Editor =
  | { type: 'role'; role?: AccessRole; clone?: boolean }
  | { type: 'team'; team?: Team }
  | { type: 'user'; user: Staff };

export const AccessFormPage = ({
  kind,
  clone = false,
  createUser = false,
}: {
  kind: Editor['type'];
  clone?: boolean;
  createUser?: boolean;
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const cache = useQueryClient();
  const access = useAccess();
  const query = useQuery({
    queryKey: ['access-administration'],
    queryFn: () => http.get<Overview>('/access'),
    enabled: canManageAccess(access.data),
  });
  const returnPath =
    '/parametres/acces?onglet=' + (kind === 'role' ? 'roles' : kind === 'team' ? 'teams' : 'users');
  const goBack = () => void navigate(returnPath);
  if (access.isPending) return <p role="status">Chargement des accès…</p>;
  if (!canManageAccess(access.data))
    return <p role="alert">Vous ne disposez pas des droits de gestion des accès.</p>;
  if (query.isError)
    return (
      <div className="access-error" role="alert">
        {query.error.message} <Button onClick={() => void query.refetch()}>Réessayer</Button>
      </div>
    );
  if (!query.data) return <p role="status">Chargement du formulaire…</p>;
  const data = query.data;
  if (createUser)
    return access.can('users', 'create') ? (
      <StaffCreateForm data={data} onClose={goBack} />
    ) : (
      <p role="alert">Vous ne pouvez pas créer d’utilisateur.</p>
    );
  const resource = kind === 'role' ? 'roles' : kind === 'team' ? 'teams' : 'users';
  const readable = access.can(resource) || (kind === 'role' && access.can('permissions'));
  if (!readable || ((!id || clone) && !access.can(resource, 'create')))
    return <p role="alert">Vous ne disposez pas de cette permission.</p>;
  const role = data.roles.find((item) => item.id === id);
  const team = data.teams.find((item) => item.id === id);
  const user = data.users.find((item) => item.id === id);
  if (
    (id && !(kind === 'role' ? role : kind === 'team' ? team : user)) ||
    (kind === 'user' && !user)
  ) {
    return (
      <div className="access-page">
        <p role="alert">Cet élément n’existe plus ou n’est pas accessible.</p>
        <div>
          <Button onClick={goBack}>Retour aux accès</Button>
        </div>
      </div>
    );
  }
  const editor: Editor =
    kind === 'role'
      ? { type: 'role', role, clone }
      : kind === 'team'
        ? { type: 'team', team }
        : { type: 'user', user: user! };
  return (
    <AccessEditor
      key={kind + ':' + (id ?? 'new') + ':' + clone}
      editor={editor}
      data={data}
      onClose={goBack}
      onSaved={async () => {
        await cache.invalidateQueries({ queryKey: ['access-administration'] });
        goBack();
        await cache.invalidateQueries({ queryKey: ['access-me'] });
      }}
    />
  );
};

const AccessEditor = ({
  editor,
  data,
  onClose,
  onSaved,
}: {
  editor: Editor;
  data: Overview;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) => {
  const access = useAccess();
  const original =
    editor.type === 'role' ? editor.role : editor.type === 'team' ? editor.team : undefined;
  const [name, setName] = useState(
    `${original?.name ?? ''}${editor.type === 'role' && editor.clone ? ' (copie)' : ''}`,
  );
  const [description, setDescription] = useState(original?.description ?? '');
  const [permissions, setPermissions] = useState<Grant[]>(
    data.resources.map(
      ({ key }) =>
        (editor.type === 'role' &&
          editor.role?.permissions.find((grant) => grant.resource === key)) || {
          resource: key,
          visibility: 'NONE',
          canCreate: false,
          canUpdate: false,
          canDelete: false,
          canValidate: false,
        },
    ),
  );
  const [members, setMembers] = useState(
    editor.type === 'team' ? (editor.team?.members.map(({ userId }) => userId) ?? []) : [],
  );
  const [memberSearch, setMemberSearch] = useState('');
  const [roleId, setRoleId] = useState(
    editor.type === 'user' ? (editor.user.accessRoleId ?? '') : '',
  );
  const protectedRole =
    editor.type === 'role' && editor.role?.systemKey === 'ADMIN' && !editor.clone;
  const targetRole =
    editor.type === 'role'
      ? editor.role
      : editor.type === 'user'
        ? data.roles.find((role) => role.id === editor.user.accessRoleId)
        : undefined;
  const delegable = !targetRole || canDelegateRole(access.data, targetRole);
  const identityEditable =
    !protectedRole &&
    delegable &&
    (editor.type === 'role'
      ? access.can('roles', !original || editor.clone ? 'create' : 'update')
      : editor.type === 'team'
        ? access.can('teams', original ? 'update' : 'create')
        : access.can('users', 'update') && access.can('permissions', 'update'));
  const permissionsEditable =
    !protectedRole &&
    delegable &&
    access.can(
      'permissions',
      editor.type === 'role' && (!original || editor.clone) ? 'create' : 'update',
    );
  const readonly = !identityEditable && !(editor.type === 'role' && permissionsEditable);
  const save = useMutation({
    mutationFn: async () => {
      if (editor.type === 'user')
        return http.put(`/access/users/${editor.user.id}/role`, {
          roleId,
          updatedAt: editor.user.updatedAt,
        });
      const id = editor.type === 'role' && editor.clone ? undefined : original?.id;
      const cleanPermissions = permissions.map(
        ({ resource, visibility, canCreate, canUpdate, canDelete, canValidate }) => ({
          resource,
          visibility,
          canCreate,
          canUpdate,
          canDelete,
          canValidate,
        }),
      );
      const body = {
        name,
        description,
        ...(id ? { updatedAt: original?.updatedAt } : {}),
        ...(editor.type === 'role' ? { permissions: cleanPermissions } : { userIds: members }),
      };
      const path = `/access/${editor.type === 'role' ? 'roles' : 'teams'}`;
      return id ? http.put(`${path}/${id}`, body) : http.post(path, body);
    },
    onSuccess: onSaved,
  });
  const updateGrant = (resource: string, patch: Partial<Grant>) =>
    setPermissions((current) =>
      current.map((grant) =>
        grant.resource === resource
          ? {
              ...grant,
              ...patch,
              ...(patch.visibility === 'NONE'
                ? { canCreate: false, canUpdate: false, canDelete: false, canValidate: false }
                : {}),
            }
          : grant,
      ),
    );
  const title =
    editor.type === 'user'
      ? `Rôle de ${editor.user.firstName} ${editor.user.lastName}`
      : editor.type === 'role'
        ? protectedRole
          ? 'Droits administrateur'
          : readonly
            ? 'Droits du rôle'
            : original && !editor.clone
              ? 'Configurer le rôle'
              : 'Créer un rôle'
        : original
          ? 'Gérer l’équipe'
          : 'Créer une équipe';
  return (
    <div className="access-page access-form-page">
      <nav className="access-breadcrumb" aria-label="Fil d’Ariane">
        <Link
          to={
            '/parametres/acces?onglet=' +
            (editor.type === 'role' ? 'roles' : editor.type === 'team' ? 'teams' : 'users')
          }
        >
          Accès et équipes
        </Link>
        <span aria-hidden>›</span>
        <span aria-current="page">{original?.name ?? title}</span>
      </nav>
      <header className="access-heading access-detail-heading">
        <span className="access-heading-icon">
          <UsersIcon />
        </span>
        <div>
          <h1>{title}</h1>
          <p>
            {editor.type === 'role'
              ? 'Définissez le périmètre et les actions autorisées pour chaque module.'
              : editor.type === 'team'
                ? 'Composez une équipe pour partager les portefeuilles de ses membres.'
                : 'Choisissez le niveau d’accès de ce collaborateur.'}
          </p>
        </div>
      </header>
      <form
        id="access-editor"
        className="access-editor access-form-card"
        onSubmit={(event) => {
          event.preventDefault();
          if (!save.isPending && !readonly) save.mutate();
        }}
      >
        {save.isError && (
          <p className="access-error" role="alert">
            {save.error.message}
          </p>
        )}
        {editor.type === 'user' ? (
          <>
            <p>
              Le nouveau rôle s’appliquera dès la prochaine requête. Les équipes restent inchangées.
            </p>
            <Select
              label="Rôle"
              required
              value={roleId}
              onChange={(event) => setRoleId(event.target.value)}
              options={data.roles
                .filter((role) => canDelegateRole(access.data, role))
                .map((role) => ({ value: role.id, label: role.name }))}
              placeholder="Choisir un rôle"
              disabled={readonly || save.isPending}
            />
          </>
        ) : (
          <>
            <div className="access-editor-identity">
              <TextField
                label={editor.type === 'role' ? 'Nom du rôle' : 'Nom de l’équipe'}
                required
                minLength={2}
                maxLength={80}
                value={name}
                onChange={(event) => setName(event.target.value)}
                disabled={!identityEditable || save.isPending}
              />
              <Textarea
                label="Description"
                maxLength={500}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                disabled={!identityEditable || save.isPending}
                rows={2}
              />
            </div>
            {editor.type === 'role' ? (
              <RolePermissionsEditor
                resources={data.resources}
                permissions={permissions}
                disabled={!permissionsEditable || save.isPending}
                onChange={updateGrant}
              />
            ) : (
              <>
                <div className="access-member-heading">
                  <h3>Membres de l’équipe</h3>
                  <span className="access-pill">{members.length} sélectionnés</span>
                </div>
                <TextField
                  label="Rechercher un membre"
                  value={memberSearch}
                  onChange={(event) => setMemberSearch(event.target.value)}
                  type="search"
                  placeholder="Nom ou adresse e-mail"
                />
                <div className="access-members">
                  {data.users
                    .filter((user) =>
                      `${user.firstName} ${user.lastName} ${user.email}`
                        .toLowerCase()
                        .includes(memberSearch.toLowerCase()),
                    )
                    .map((user) => (
                      <div className="access-member" key={user.id}>
                        <Checkbox
                          label={`${user.firstName} ${user.lastName}`}
                          checked={members.includes(user.id)}
                          disabled={readonly || save.isPending}
                          onChange={(event) =>
                            setMembers((current) =>
                              event.target.checked
                                ? [...current, user.id]
                                : current.filter((id) => id !== user.id),
                            )
                          }
                        />
                        <small>{user.email}</small>
                      </div>
                    ))}
                </div>
              </>
            )}
          </>
        )}
      </form>
      <div className="access-form-footer">
        <Button variant="secondary" disabled={save.isPending} onClick={onClose}>
          {readonly ? 'Retour' : 'Annuler'}
        </Button>
        {!readonly && (
          <Button
            type="submit"
            form="access-editor"
            loading={save.isPending}
            loadingLabel="Enregistrement…"
          >
            Enregistrer
          </Button>
        )}
      </div>
    </div>
  );
};
